import { supabase } from '../config/supabase';

interface DadosEmpresa {
  cnpj: string;
  razao_social: string;
  nome_fantasia?: string;
  cnae_principal: {
    codigo: string;
    descricao: string;
  };
  cnaes_secundarios?: Array<{
    codigo: string;
    descricao: string;
  }>;
  endereco: {
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    municipio: string;
    uf: string;
    cep: string;
  };
  situacao: string;
  data_situacao: string;
  porte: string;
  natureza_juridica: string;
  telefone?: string;
  email?: string;
}

export const consultarCNPJ = async (cnpj: string): Promise<DadosEmpresa | null> => {
  try {
    // Remove formatação do CNPJ
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    
    if (cnpjLimpo.length !== 14) {
      throw new Error('CNPJ deve ter 14 dígitos');
    }

    // Tenta primeiro a BrasilAPI
    let response;
    let data;
    
    try {
      response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`);
      
      if (!response.ok) {
        throw new Error('Erro ao consultar CNPJ na BrasilAPI');
      }

      data = await response.json();
      
      if (data.status == 'ERROR' || !data.cnpj) {
        throw new Error(data.message || 'CNPJ não encontrado na BrasilAPI');
      }
    } catch (brasilApiError) {
      console.warn('BrasilAPI falhou, tentando API alternativa:', brasilApiError);
      
      // Fallback para API alternativa com CORS
      try {
        response = await fetch(`https://api.cnpjs.com.br/v1/cnpj/${cnpjLimpo}`, {
          headers: {
            'Accept': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error('Erro ao consultar CNPJ na API alternativa');
        }

        data = await response.json();
        
        if (!data.cnpj) {
          throw new Error('CNPJ não encontrado na API alternativa');
        }
      } catch (altApiError) {
        console.warn('API alternativa também falhou:', altApiError);
        throw new Error('Não foi possível consultar o CNPJ. Tente novamente mais tarde.');
      }
    }

    // Mapeia os dados da BrasilAPI para nosso formato
    const dadosEmpresa: DadosEmpresa = {
      cnpj: data.cnpj,
      razao_social: data.razao_social,
      nome_fantasia: data.nome_fantasia || undefined,
      cnae_principal: {
        codigo: data.cnae_fiscal?.toString() || '',
        descricao: data.cnae_fiscal_descricao || ''
      },
      cnaes_secundarios: data.cnaes_secundarios?.map((cnae: any) => ({
        codigo: cnae.codigo,
        descricao: cnae.descricao
      })) || [],
      endereco: {
        logradouro: `${data.descricao_tipo_logradouro || ''} ${data.logradouro || ''}`.trim(),
        numero: data.numero || '',
        complemento: data.complemento || undefined,
        bairro: data.bairro || '',
        municipio: data.municipio || '',
        uf: data.uf || '',
        cep: data.cep || ''
      },
      situacao: data.descricao_situacao_cadastral || '',
      data_situacao: data.data_situacao_cadastral || '',
      porte: data.porte || '',
      natureza_juridica: data.natureza_juridica || '',
      telefone: data.ddd_telefone_1 || undefined,
      email: data.email || undefined
    };

    return dadosEmpresa;
  } catch (error) {
    console.error('Erro ao consultar CNPJ:', error);
    return null;
  }
};

export const obterGrauRiscoPorCNAE = async (codigoCNAE: string): Promise<number> => {
  try {  
    // Remove formatação (pontos, hífens) e extrai apenas os primeiros 5 dígitos do CNAE
    // Exemplo: "46.851-00" -> "46851"
    const cnaeLimpo = codigoCNAE.replace(/\D/g, ''); // Remove tudo que não é dígito
    const cnaeParaBusca = cnaeLimpo.substring(0, 5); // Pega apenas os primeiros 5 dígitos

    // Busca o grau de risco na tabela cp_cnae do banco de dados
    // Primeiro tenta busca exata
    let { data, error } = await supabase
      .from('cp_cnae')
      .select('grau_risco, e_mineracao, grupo_mineracao, tipo_comissao')
      .eq('cnae', cnaeParaBusca)
      .maybeSingle();

    if (error) {
      console.error(`❌ Erro ao buscar CNAE ${cnaeParaBusca}:`, error);
      return 2; // Grau 2 (médio risco) como padrão
    }

    // Se não encontrou busca exata, tenta busca com LIKE nos 4 primeiros dígitos
    if (!data && cnaeParaBusca.length >= 4) {
      const cnae4Digitos = cnaeParaBusca.substring(0, 4);
      
      const { data: dataLike, error: errorLike } = await supabase
        .from('cp_cnae')
        .select('grau_risco, e_mineracao, grupo_mineracao, tipo_comissao')
        .like('cnae', `%${cnae4Digitos}%`)
        .limit(1)
        .single();

      if (errorLike) {
        console.warn(`⚠️ Busca LIKE também falhou para ${cnae4Digitos}:`, errorLike);
      } else if (dataLike) {
        // Log com tipo de comissão
        console.log(`🏗️ CNAE ${cnae4Digitos} encontrado:`, {
          grau_risco: dataLike.grau_risco,
          tipo_comissao: dataLike.tipo_comissao,
          e_mineracao: dataLike.e_mineracao,
          grupo_mineracao: dataLike.grupo_mineracao
        });
        return dataLike.grau_risco;
      }
    }

    if (!data) {
      console.warn(`⚠️ CNAE ${cnaeParaBusca} não encontrado na tabela cp_cnae (busca exata e LIKE), usando grau 2 como padrão`);
      return 2; // Grau 2 (médio risco) como padrão
    }

    // Log com tipo de comissão
    console.log(`🏗️ CNAE ${cnaeParaBusca} encontrado:`, {
      grau_risco: data.grau_risco,
      tipo_comissao: data.tipo_comissao,
      e_mineracao: data.e_mineracao,
      grupo_mineracao: data.grupo_mineracao
    });

    return data.grau_risco;
  } catch (error) {
    console.error('❌ Erro ao buscar grau de risco por CNAE:', error);
    return 2; // Grau 2 (médio risco) como padrão em caso de erro
  }
};

/**
 * Busca o tipo de comissão baseado no CNAE na tabela cp_cnae
 * @param codigoCNAE - Código CNAE (pode vir formatado: "46.851-00" ou limpo: "46851")
 * @returns 'CIPA' | 'CIPAMIN' | 'CIPATR' do banco de dados
 */
export const obterTipoComissaoPorCNAE = async (codigoCNAE: string): Promise<'CIPA' | 'CIPAMIN' | 'CIPATR'> => {
  try {  
    // Remove formatação (pontos, hífens) e extrai apenas os primeiros 5 dígitos do CNAE
    const cnaeLimpo = codigoCNAE.replace(/\D/g, '');
    const cnaeParaBusca = cnaeLimpo.substring(0, 5);

    // Busca o tipo_comissao na tabela cp_cnae do banco de dados
    // Primeiro tenta busca exata
    let { data, error } = await supabase
      .from('cp_cnae')
      .select('tipo_comissao')
      .eq('cnae', cnaeParaBusca)
      .maybeSingle();

    if (error) {
      console.error(`❌ Erro ao buscar tipo comissão para CNAE ${cnaeParaBusca}:`, error);
      return 'CIPA'; // CIPA como padrão
    }

    // Se não encontrou busca exata, tenta busca com LIKE nos 4 primeiros dígitos
    if (!data && cnaeParaBusca.length >= 4) {
      const cnae4Digitos = cnaeParaBusca.substring(0, 4);
      
      const { data: dataLike, error: errorLike } = await supabase
        .from('cp_cnae')
        .select('tipo_comissao')
        .like('cnae', `%${cnae4Digitos}%`)
        .limit(1)
        .single();

      if (errorLike) {
        console.warn(`⚠️ Busca LIKE falhou para ${cnae4Digitos}:`, errorLike);
      } else if (dataLike) {
        console.log(`✅ Tipo comissão encontrado via LIKE: ${cnae4Digitos} -> ${dataLike.tipo_comissao}`);
        return dataLike.tipo_comissao as 'CIPA' | 'CIPAMIN' | 'CIPATR';
      }
    }

    if (!data) {
      console.warn(`⚠️ CNAE ${cnaeParaBusca} não encontrado, usando CIPA como padrão`);
      return 'CIPA';
    }

    console.log(`✅ Tipo comissão encontrado: ${cnaeParaBusca} -> ${data.tipo_comissao}`);
    return data.tipo_comissao as 'CIPA' | 'CIPAMIN' | 'CIPATR';
  } catch (error) {
    console.error('❌ Erro ao buscar tipo comissão por CNAE:', error);
    return 'CIPA';
  }
};

export const formatarEndereco = (endereco: DadosEmpresa['endereco']): string => {
  const partes = [
    endereco.logradouro,
    endereco.numero,
    endereco.complemento,
    endereco.bairro,
    endereco.municipio,
    endereco.uf,
    endereco.cep
  ].filter(Boolean);
  
  return partes.join(', ');
};

interface DimensionamentoCIPA {
  efetivos: number;
  suplentes: number;
  total: number;
  // campos extras para auditoria
  baseTabela: number;
  blocos: number;
  acrescimoPorBloco: number;
  acrescimoTotal: number;
}

export const calcularDimensionamentoCIPA = (
  grauRisco: number,
  numeroFuncionarios: number
): DimensionamentoCIPA => {

  if (numeroFuncionarios < 20) {
    return {
      efetivos: 0,
      suplentes: 0,
      total: 0,
      baseTabela: 0,
      blocos: 0,
      acrescimoPorBloco: 0,
      acrescimoTotal: 0
    };
  }

  let baseEfetivos = 0;
  let baseSuplentes = 0;
  let blocos = 0;
  let acrescimoPorBloco = 0;
  let acrescimoTotal = 0;

  // ---- Até 10.000 funcionários ----
  if (numeroFuncionarios >= 20 && numeroFuncionarios <= 29) {
    if (grauRisco == 3) { baseEfetivos = 1; baseSuplentes = 1; }
    if (grauRisco == 4) { baseEfetivos = 1; baseSuplentes = 1; }
  }

  if (numeroFuncionarios >= 30 && numeroFuncionarios <= 50) {
    if (grauRisco == 3) { baseEfetivos = 1; baseSuplentes = 1; }
    if (grauRisco == 4) { baseEfetivos = 2; baseSuplentes = 1; }
  }

  if (numeroFuncionarios >= 51 && numeroFuncionarios <= 80) {
    if (grauRisco == 2) { baseEfetivos = 1; baseSuplentes = 1; }
    if (grauRisco == 3) { baseEfetivos = 2; baseSuplentes = 1; }
    if (grauRisco == 4) { baseEfetivos = 3; baseSuplentes = 2; }
  }

  if (numeroFuncionarios >= 81 && numeroFuncionarios <= 100) {
    if (grauRisco == 1) { baseEfetivos = 1; baseSuplentes = 1; }
    if (grauRisco == 2) { baseEfetivos = 1; baseSuplentes = 1; }
    if (grauRisco == 3) { baseEfetivos = 2; baseSuplentes = 1; }
    if (grauRisco == 4) { baseEfetivos = 3; baseSuplentes = 2; }
  }

  if (numeroFuncionarios >= 101 && numeroFuncionarios <= 120) {
    if (grauRisco == 1) { baseEfetivos = 1; baseSuplentes = 1; }
    if (grauRisco == 2) { baseEfetivos = 2; baseSuplentes = 1; }
    if (grauRisco == 3) { baseEfetivos = 2; baseSuplentes = 1; }
    if (grauRisco == 4) { baseEfetivos = 4; baseSuplentes = 2; }
  }

  if (numeroFuncionarios >= 121 && numeroFuncionarios <= 140) {
    if (grauRisco == 1) { baseEfetivos = 1; baseSuplentes = 1; }
    if (grauRisco == 2) { baseEfetivos = 2; baseSuplentes = 1; }
    if (grauRisco == 3) { baseEfetivos = 3; baseSuplentes = 2; }
    if (grauRisco == 4) { baseEfetivos = 4; baseSuplentes = 2; }
  }

  if (numeroFuncionarios >= 141 && numeroFuncionarios <= 300) {
    if (grauRisco == 1) { baseEfetivos = 1; baseSuplentes = 1; }
    if (grauRisco == 2) { baseEfetivos = 3; baseSuplentes = 2; }
    if (grauRisco == 3) { baseEfetivos = 4; baseSuplentes = 2; }
    if (grauRisco == 4) { baseEfetivos = 4; baseSuplentes = 3; }
  }

  if (numeroFuncionarios >= 301 && numeroFuncionarios <= 500) {
    if (grauRisco == 1) { baseEfetivos = 2; baseSuplentes = 2; }
    if (grauRisco == 2) { baseEfetivos = 4; baseSuplentes = 3; }
    if (grauRisco == 3) { baseEfetivos = 5; baseSuplentes = 4; }
    if (grauRisco == 4) { baseEfetivos = 5; baseSuplentes = 4; }
  }

  if (numeroFuncionarios >= 501 && numeroFuncionarios <= 1000) {
    if (grauRisco == 1) { baseEfetivos = 4; baseSuplentes = 3; }
    if (grauRisco == 2) { baseEfetivos = 5; baseSuplentes = 4; }
    if (grauRisco == 3) { baseEfetivos = 6; baseSuplentes = 4; }
    if (grauRisco == 4) { baseEfetivos = 6; baseSuplentes = 5; }
  }

  if (numeroFuncionarios >= 2001 && numeroFuncionarios <= 3500) {
    if (grauRisco == 1) { baseEfetivos = 8; baseSuplentes = 8; }
    if (grauRisco == 2) { baseEfetivos = 9; baseSuplentes = 8; }
    if (grauRisco == 3) { baseEfetivos = 8; baseSuplentes = 6; }
    if (grauRisco == 4) { baseEfetivos = 9; baseSuplentes = 7; }
  }

  if (numeroFuncionarios >= 1001 && numeroFuncionarios <= 2500) {
    if (grauRisco == 1) { baseEfetivos = 5; baseSuplentes = 4; }
    if (grauRisco == 2) { baseEfetivos = 6; baseSuplentes = 5; }
    if (grauRisco == 3) { baseEfetivos = 8; baseSuplentes = 8; }
    if (grauRisco == 4) { baseEfetivos = 9; baseSuplentes = 7; }
  }

  if (numeroFuncionarios >= 2501 && numeroFuncionarios <= 5000) {
    if (grauRisco == 1) { baseEfetivos = 6; baseSuplentes = 5; }
    if (grauRisco == 2) { baseEfetivos = 8; baseSuplentes = 6; }
    if (grauRisco == 3) { baseEfetivos = 10; baseSuplentes = 8; }
    if (grauRisco == 4) { baseEfetivos = 11; baseSuplentes = 8; }
  }

  if (numeroFuncionarios >= 5001 && numeroFuncionarios <= 10000) {
    if (grauRisco == 1) { baseEfetivos = 8; baseSuplentes = 6; }
    if (grauRisco == 2) { baseEfetivos = 10; baseSuplentes = 8; }
    if (grauRisco == 3) { baseEfetivos = 12; baseSuplentes = 8; }
    if (grauRisco == 4) { baseEfetivos = 13; baseSuplentes = 10; }
  }

  // ---- Acima de 10.000 ----
  if (numeroFuncionarios > 10000) {
    if (grauRisco == 1) { baseEfetivos = 8; baseSuplentes = 6; acrescimoPorBloco = 1; }
    if (grauRisco == 2) { baseEfetivos = 10; baseSuplentes = 8; acrescimoPorBloco = 1; }
    if (grauRisco == 3) { baseEfetivos = 12; baseSuplentes = 8; acrescimoPorBloco = 2; }
    if (grauRisco == 4) { baseEfetivos = 13; baseSuplentes = 10; acrescimoPorBloco = 2; }

    const excedente = numeroFuncionarios - 10000;
    blocos = Math.ceil(excedente / 2500);
    acrescimoTotal = blocos * acrescimoPorBloco;
  }

  const efetivosFinal = baseEfetivos + acrescimoTotal;
  const suplentesFinal = baseSuplentes + acrescimoTotal;

  return {
    efetivos: efetivosFinal,
    suplentes: suplentesFinal,
    total: efetivosFinal + suplentesFinal,
    baseTabela: baseEfetivos,
    blocos,
    acrescimoPorBloco,
    acrescimoTotal
  };
};


export const formatarCNAE = (codigo: string): string => {
  if (!codigo) return '';
  
  // Remove todos os caracteres não numéricos
  const codigoLimpo = codigo.replace(/\D/g, '');
  
  // Se já está formatado, retorna como está
  if (codigo.includes('.') && codigo.includes('-')) {
    return codigo;
  }
  
  // Formata como XX.XX-X
  if (codigoLimpo.length >= 7) {
    return `${codigoLimpo.slice(0, 2)}.${codigoLimpo.slice(2, 5)}-${codigoLimpo.slice(5, 7)}`;
  }
  
  // Se tem menos de 7 dígitos, adiciona zeros à esquerda
  if (codigoLimpo.length < 7) {
    const codigoCompleto = codigoLimpo.padStart(7, '0');
    return `${codigoCompleto.slice(0, 2)}.${codigoCompleto.slice(2, 5)}-${codigoCompleto.slice(5, 7)}`;
  }
  
  return codigo;
};

export type { DadosEmpresa };
